/** @module @liquidefy/code-editor/circuits/ai-assisted-editor @version 1.0.0 @license LQD Research LLC */

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import type {
  Position,
  Range,
  Selection,
  EditorResult,
  DocumentState,
  AIRequest,
  AIResponse,
  AIContext,
  LanguageId,
  SkillLevel,
} from '../code-editor-types.js';

import { ok, err, generateId, now, asDocumentId, asLanguageId } from '../code-editor.js';

import { createDocument } from '../document/document.js';
import { applyEditToContent, createReplaceEdit, createInsertEdit } from '../document/edit.js';

import { createRequest, createCompletionRequest, createAnalysisRequest, createExplainRequest, createFixRequest, createRefactorRequest } from '../ai/core/request.js';
import { extractText, extractCode, extractSuggestions, extractAnalysis, assessResponseQuality } from '../ai/core/response.js';

import { quickAnalysis, securityAnalysis, fileComplexity, computeMaintainabilityIndex } from '../ai/agents/analysis.js';
import { buildGenerationContext, assessQuality } from '../ai/agents/generation.js';

import { createContextConfig, createContextWindow, addContextItem, extractCursorContext, extractImportContext, extractSymbolContext, packContext, contextToString } from '../ai/prompts/context.js';
import { buildCompletionPrompt, buildAnalysisPrompt, buildExplainPrompt, buildFixPrompt, buildRefactorPrompt } from '../ai/prompts/strategies.js';


// ═══════════════════════════════════════════════════════════════════════════════
// §1 CONFIG & HANDLE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

export interface AIAssistedEditorConfig {
  readonly uri: string;
  readonly content: string;
  readonly language?: string;
  readonly aiEnabled?: boolean;
  readonly qualityThreshold?: number;
}

export interface AICompletionResult {
  readonly suggestions: string[];
  readonly confidence: number;
}

export interface AICodeAnalysis {
  readonly score: number;
  readonly findings: string[];
}

export interface AISecurityAssessment {
  readonly score: number;
  readonly vulnerabilities: string[];
}

export interface AIRequestStats {
  readonly requests: number;
  readonly acceptRate: number;
}

export interface AIAssistedEditorHandle extends CircuitHandle {
  getDocument(): DocumentState;
  edit(position: Position, text: string): EditorResult<DocumentState>;
  requestCompletion(): AICompletionResult;
  requestExplanation(range: Range): string;
  requestFix(errorMessage: string): EditorResult<DocumentState>;
  requestRefactor(instruction: string): EditorResult<DocumentState>;
  analyzeCode(): AICodeAnalysis;
  assessSecurity(): AISecurityAssessment;
  getAIStats(): AIRequestStats;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §2 INTERNAL STATE
// ═══════════════════════════════════════════════════════════════════════════════

interface InternalAIState {
  document: DocumentState;
  aiEnabled: boolean;
  qualityThreshold: number;
  uri: string;
  requestCount: number;
  acceptCount: number;
  lastCompletions: string[];
  lastAnalysis: AICodeAnalysis | null;
  lastSecurity: AISecurityAssessment | null;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §3 PURE HELPERS — build context, simulate AI round-trips
// ═══════════════════════════════════════════════════════════════════════════════

function buildEditorContext(state: InternalAIState, position?: Position, selection?: Selection): AIContext {
  return {
    fileUri: state.uri,
    language: state.document.language,
    cursorPosition: position,
    selection,
    imports: extractImportContext(state.document.content).content.split('\n').filter(Boolean),
    symbols: extractSymbolContext(state.document.content).content.split('\n').filter(Boolean),
  };
}

function buildContextWindow(state: InternalAIState, position?: Position): ReturnType<typeof createContextWindow> {
  const config = createContextConfig();
  let window = createContextWindow(config.maxTokens);

  if (position) {
    const cursorCtx = extractCursorContext(state.document.content, position.line, 20);
    window = addContextItem(window, cursorCtx);
  }

  const importCtx = extractImportContext(state.document.content);
  window = addContextItem(window, importCtx);

  const symbolCtx = extractSymbolContext(state.document.content);
  window = addContextItem(window, symbolCtx);

  return window;
}

function simulateCompletionResponse(content: string, language: LanguageId): string[] {
  const lines = content.split('\n');
  const lastLine = (lines[lines.length - 1] || '').trim();

  const suggestions: string[] = [];

  if (lastLine.endsWith('{')) {
    suggestions.push('\n  \n}');
    suggestions.push('\n  // TODO: implement\n}');
  } else if (lastLine.startsWith('function') || lastLine.startsWith('export function')) {
    suggestions.push('(): void {\n  \n}');
    suggestions.push('<T>(value: T): T {\n  return value;\n}');
  } else if (lastLine.startsWith('const') || lastLine.startsWith('let')) {
    suggestions.push(" = '';");
    suggestions.push(' = 0;');
    suggestions.push(' = [];');
  } else if (lastLine.startsWith('import')) {
    suggestions.push(" from './module.js';");
  } else {
    suggestions.push('// continue implementation');
    suggestions.push('return;');
  }

  return suggestions;
}

function simulateExplanation(content: string, range: Range): string {
  const lines = content.split('\n');
  const startLine = Math.max(0, range.start.line);
  const endLine = Math.min(lines.length - 1, range.end.line);
  const selectedLines = lines.slice(startLine, endLine + 1);
  const lineCount = selectedLines.length;

  const hasFunction = selectedLines.some(l => /function\s|=>/.test(l));
  const hasLoop = selectedLines.some(l => /for\s|while\s|\.forEach|\.map|\.reduce/.test(l));
  const hasConditional = selectedLines.some(l => /if\s|switch\s|ternary|\?/.test(l));

  const parts: string[] = [`This code spans ${lineCount} line(s).`];

  if (hasFunction) parts.push('It defines or invokes a function.');
  if (hasLoop) parts.push('It contains iteration logic.');
  if (hasConditional) parts.push('It includes conditional branching.');

  if (!hasFunction && !hasLoop && !hasConditional) {
    parts.push('It performs sequential operations without branching or iteration.');
  }

  return parts.join(' ');
}

function simulateFix(content: string, errorMessage: string): string {
  if (errorMessage.includes('missing semicolon') || errorMessage.includes('Expected ;')) {
    return content.replace(/([^;{}\s])\s*\n/g, '$1;\n');
  }
  if (errorMessage.includes('undefined') || errorMessage.includes('not defined')) {
    return `// Fixed: added missing declarations\n${content}`;
  }
  if (errorMessage.includes('type') || errorMessage.includes('Type')) {
    return `// Fixed: corrected type annotations\n${content}`;
  }
  return `// AI-suggested fix for: ${errorMessage}\n${content}`;
}

function simulateRefactor(content: string, instruction: string): string {
  if (instruction.includes('extract') || instruction.includes('function')) {
    return `// Refactored: extracted function per instruction\nfunction extracted() {\n${content}\n}\n`;
  }
  if (instruction.includes('rename') || instruction.includes('naming')) {
    return `// Refactored: improved naming conventions\n${content}`;
  }
  if (instruction.includes('simplify') || instruction.includes('clean')) {
    return `// Refactored: simplified logic\n${content}`;
  }
  return `// Refactored per: ${instruction}\n${content}`;
}

function analyzeCodeQuality(content: string): AICodeAnalysis {
  const findings: string[] = [];
  const lines = content.split('\n');

  const complexity = fileComplexity(content);
  const maintainability = computeMaintainabilityIndex(complexity.halstead, complexity.cyclomatic, lines.length);

  if (complexity.cyclomatic > 10) findings.push(`High cyclomatic complexity: ${complexity.cyclomatic}`);
  if (complexity.cognitive > 15) findings.push(`High cognitive complexity: ${complexity.cognitive}`);
  if (lines.length > 300) findings.push(`File is long (${lines.length} lines), consider splitting`);

  const longLines = lines.filter(l => l.length > 120).length;
  if (longLines > 0) findings.push(`${longLines} line(s) exceed 120 characters`);

  const todoCount = lines.filter(l => /TODO|FIXME|HACK|XXX/.test(l)).length;
  if (todoCount > 0) findings.push(`${todoCount} TODO/FIXME comment(s) found`);

  const emptyBlocks = (content.match(/\{\s*\}/g) || []).length;
  if (emptyBlocks > 0) findings.push(`${emptyBlocks} empty block(s) detected`);

  if (findings.length === 0) findings.push('No significant issues found');

  const score = Math.max(0, Math.min(100, maintainability));

  return { score: Math.round(score), findings };
}

function assessSecurityInternal(content: string): AISecurityAssessment {
  const vulnerabilities: string[] = [];

  if (/eval\s*\(/.test(content)) vulnerabilities.push('Use of eval() detected — potential code injection risk');
  if (/innerHTML\s*=/.test(content)) vulnerabilities.push('innerHTML assignment — potential XSS vulnerability');
  if (/document\.write/.test(content)) vulnerabilities.push('document.write usage — potential XSS risk');
  if (/new\s+Function\(/.test(content)) vulnerabilities.push('Dynamic Function constructor — potential injection risk');
  if (/process\.env/.test(content) && /console\.(log|info|debug)/.test(content)) {
    vulnerabilities.push('Environment variables may be exposed through console logging');
  }
  if (/password|secret|token|api_key|apikey/i.test(content) && /=\s*['"`]/.test(content)) {
    vulnerabilities.push('Possible hardcoded credential detected');
  }
  if (/http:\/\//.test(content)) vulnerabilities.push('Non-HTTPS URL detected — potential data exposure');
  if (/dangerouslySetInnerHTML/.test(content)) vulnerabilities.push('React dangerouslySetInnerHTML — review for XSS');

  const score = vulnerabilities.length === 0 ? 100
    : vulnerabilities.length <= 2 ? 75
    : vulnerabilities.length <= 4 ? 50
    : 25;

  if (vulnerabilities.length === 0) vulnerabilities.push('No known vulnerabilities detected');

  return { score, vulnerabilities };
}


// ═══════════════════════════════════════════════════════════════════════════════
// §4 FACTORY — createAIAssistedEditor
// ═══════════════════════════════════════════════════════════════════════════════

export function createAIAssistedEditor(config: AIAssistedEditorConfig): AIAssistedEditorHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // Create initial document
  const docResult = createDocument(config.content, config.language ?? 'typescript');
  if (!docResult.success) throw new Error(`Failed to create document: ${docResult.message}`);

  const state: InternalAIState = {
    document: docResult.value,
    aiEnabled: config.aiEnabled !== false,
    qualityThreshold: config.qualityThreshold ?? 0.7,
    uri: config.uri,
    requestCount: 0,
    acceptCount: 0,
    lastCompletions: [],
    lastAnalysis: null,
    lastSecurity: null,
  };

  function updateDocument(newContent: string): DocumentState {
    const updated: DocumentState = {
      ...state.document,
      content: newContent,
      version: state.document.version + 1,
      isDirty: true,
      lineCount: newContent.split('\n').length,
      charCount: newContent.length,
    };
    state.document = updated;
    return updated;
  }

  return Object.freeze({
    name: 'ai-assisted-editor',

    getDocument(): DocumentState {
      guardDestroyed();
      return state.document;
    },

    edit(position: Position, text: string): EditorResult<DocumentState> {
      guardDestroyed();
      try {
        const editOp = createInsertEdit(position, text);
        const newContent = applyEditToContent(state.document.content, editOp);
        const doc = updateDocument(newContent);
        circuitStats = recordSuccess(circuitStats);
        return ok(doc, 'EDIT_APPLIED', 'Edit applied successfully');
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('EDIT_FAILED', `Edit failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    requestCompletion(): AICompletionResult {
      guardDestroyed();
      state.requestCount++;

      if (!state.aiEnabled) {
        circuitStats = recordSuccess(circuitStats);
        return { suggestions: [], confidence: 0 };
      }

      try {
        const ctx = buildEditorContext(state);
        const window = buildContextWindow(state);
        const suggestions = simulateCompletionResponse(state.document.content, state.document.language);
        const confidence = suggestions.length > 0 ? 0.8 : 0.2;

        state.lastCompletions = suggestions;
        circuitStats = recordSuccess(circuitStats);
        return { suggestions, confidence };
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return { suggestions: [], confidence: 0 };
      }
    },

    requestExplanation(range: Range): string {
      guardDestroyed();
      state.requestCount++;

      if (!state.aiEnabled) {
        circuitStats = recordSuccess(circuitStats);
        return 'AI assistance is disabled.';
      }

      try {
        const explanation = simulateExplanation(state.document.content, range);
        circuitStats = recordSuccess(circuitStats);
        return explanation;
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return `Explanation failed: ${e instanceof Error ? e.message : String(e)}`;
      }
    },

    requestFix(errorMessage: string): EditorResult<DocumentState> {
      guardDestroyed();
      state.requestCount++;

      if (!state.aiEnabled) {
        return err('AI_DISABLED', 'AI assistance is disabled');
      }

      try {
        const fixedContent = simulateFix(state.document.content, errorMessage);
        const doc = updateDocument(fixedContent);
        state.acceptCount++;
        circuitStats = recordSuccess(circuitStats);
        return ok(doc, 'FIX_APPLIED', 'AI fix applied successfully');
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('FIX_FAILED', `Fix failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    requestRefactor(instruction: string): EditorResult<DocumentState> {
      guardDestroyed();
      state.requestCount++;

      if (!state.aiEnabled) {
        return err('AI_DISABLED', 'AI assistance is disabled');
      }

      try {
        const refactoredContent = simulateRefactor(state.document.content, instruction);
        const doc = updateDocument(refactoredContent);
        state.acceptCount++;
        circuitStats = recordSuccess(circuitStats);
        return ok(doc, 'REFACTOR_APPLIED', 'AI refactor applied successfully');
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('REFACTOR_FAILED', `Refactor failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    analyzeCode(): AICodeAnalysis {
      guardDestroyed();
      state.requestCount++;

      try {
        const analysis = analyzeCodeQuality(state.document.content);
        state.lastAnalysis = analysis;
        circuitStats = recordSuccess(circuitStats);
        return analysis;
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return { score: 0, findings: ['Analysis failed'] };
      }
    },

    assessSecurity(): AISecurityAssessment {
      guardDestroyed();
      state.requestCount++;

      try {
        const assessment = assessSecurityInternal(state.document.content);
        state.lastSecurity = assessment;
        circuitStats = recordSuccess(circuitStats);
        return assessment;
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return { score: 0, vulnerabilities: ['Security assessment failed'] };
      }
    },

    getAIStats(): AIRequestStats {
      guardDestroyed();
      return {
        requests: state.requestCount,
        acceptRate: state.requestCount > 0 ? state.acceptCount / state.requestCount : 0,
      };
    },

    stats(): CircuitStats {
      return { ...circuitStats, uptime: Date.now() - createdAt };
    },

    health(): CircuitHealthReport {
      if (destroyed) return buildHealthReport('ai-assisted-editor', 'destroyed', 'Circuit destroyed', createdAt);
      return buildHealthReport('ai-assisted-editor', healthFromStats(circuitStats), formatStatsDetails(circuitStats), createdAt);
    },

    destroy(): void {
      destroyed = true;
    },
  });
}
